import { FC, useRef, useMemo, useState, type UIEvent, type Ref } from 'react';
import classNames from 'classnames';

import PointListItemContent from 'application/pages/AdsPage/renderer/AdsDrawer/PointList/PointListItemContent';
import AnalyticsService, { EventNameAMP, ParamNameAMP } from 'application/services/AnalyticsService';
import { useCompanyTagsContext } from 'application/components/common';
import { useAccountCompanyId } from 'application/hooks';
import usePromoState from 'application/hooks/usePromoState';
import { ADS_PAGE_PATH } from 'application/pages/AdsPage';
import { useApolloClient } from 'application/providers/DataProvider';
import { useAdCampaignCreate, useAdCampaignSetTags, serializePromoAdInputs } from '@entities/adCampaign';
import { RouterUtils, useNavigate } from 'application/providers/RouterProvider';
import { DEFAULT_ERROR_MESSAGE } from '@shared/config';
import { useAppDispatch, useAppSelector } from '@shared/store';
import TagFilters, { ITagFiltersState } from 'application/components/MainToolbar/FiltersTool/TagFilters';
import CompanyTagsList from 'application/components/MainToolbar/FiltersTool/TagFilters/CompanyTagsList';
import { VISIBLE_TITLE_LENGTH, VISIBLE_TITLE_LENGTH_MOBILE } from 'application/components/CompanyTagChip';

import { AdCampaignMediaTypeEnum } from '@shared/api/graphql/generated';
import { AdCampaignTypeEnum } from '@entities/media';

import DateUtils from '@zvuk-b2b/react-uikit/toolkit/DateUtils';
import TimezoneUtils from '@zvuk-b2b/react-uikit/toolkit/TimezoneUtils';
import useMedia from '@zvuk-b2b/react-uikit/toolkit/useMedia';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import InputSearch from '@zvuk-b2b/react-uikit/ui/InputSearch';
import Switcher from '@zvuk-b2b/react-uikit/ui/Switcher';
import { ResponsiveDialog } from '@zvuk-b2b/ui-kit';
import { toast } from '@shared/ui';
import { areVideoPromosValid, projectVideoPromoDraft } from '../../AdCampaignMedia/videoPromoDraft';

import { NAME_MAX_LENGTH_ERROR, NOTIFICATIONS_TEXT } from '../../consts';
import {
  selectCampaignName,
  selectCampaignType,
  selectDayEndTime,
  selectDayStartTime,
  selectEndDate,
  selectIsInfinitely,
  selectSelectedPointIds,
  selectStartDate,
  selectValueByMode,
  selectWeekdays,
} from '../../../slices/createAdCampaign/selectors';
import {
  resetCreateAdCampaign,
  setSelectedPointIds as saveSelectedPointIds,
} from '../../../slices/createAdCampaign/slice';
import styles from './index.module.scss';
import { TEXTS } from './texts';
import { useAdCampaignStepperPointsViewModel } from './useAdCampaignStepperPointsViewModel';

export type AdCampaignStepperPointsProps = {
  mediaType?: AdCampaignMediaTypeEnum;
  onBack?: () => void;
  close?: () => void;
  promoState: ReturnType<typeof usePromoState>;
  timeById: Record<string, string>;
  durationById: Record<string, string>;
  canBackToPromoRollSynth?: boolean;
};

const SKELETON_ACTION_KEYS = ['select-all', 'separator', 'reset'];
const SKELETON_ITEM_KEYS = ['first', 'second', 'third', 'fourth', 'fifth'];

const AdCampaignStepperPoints: FC<AdCampaignStepperPointsProps> = ({
  mediaType,
  onBack,
  close,
  promoState,
  timeById,
  durationById,
  canBackToPromoRollSynth,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const companyId = useAccountCompanyId();
  const client = useApolloClient();
  const [createAdCampaign, adCampaignCreateResult] = useAdCampaignCreate();
  const [adCampaignSetTagsRequest] = useAdCampaignSetTags();
  const adCampaignCreateLoading = adCampaignCreateResult.loading;

  const adCampaignSetTags = (adCampaignId: string, tagNames: string[]) =>
    adCampaignSetTagsRequest({ adCampaignId, tagNames });

  const clearAdCampaigns = () => {
    client.cache.evict({
      id: 'ROOT_QUERY',
      fieldName: 'adcampaignsPaginationQuery',
      broadcast: false,
    });
  };

  const { onSetTagsSubmit } = useCompanyTagsContext();

  const campaignName = useAppSelector(selectCampaignName);
  const campaignType = useAppSelector(selectCampaignType);
  const valueByMode = useAppSelector(selectValueByMode);
  const startDateStr = useAppSelector(selectStartDate);
  const endDateStr = useAppSelector(selectEndDate);
  const isInfinitely = useAppSelector(selectIsInfinitely);
  const weekdays = useAppSelector(selectWeekdays);
  const dayStartTime = useAppSelector(selectDayStartTime);
  const dayEndTime = useAppSelector(selectDayEndTime);

  const { isDesktop } = useMedia();
  const listRef = useRef<HTMLInputElement | null>(null);

  const savedPointIds = useAppSelector(selectSelectedPointIds);

  const { isLoading, loadMore, points } = useAdCampaignStepperPointsViewModel({ mediaType });
  const [searchQuery, setSearchQuery] = useState('');
  const [companyListTagsProps, setCompanyListTagsProps] = useState<ITagFiltersState>({ selectedTags: [] });
  const [selectedPointIds, setSelectedPointIds] = useState<Set<string>>(() => new Set(savedPointIds));
  const [isCreating, setIsCreating] = useState(false);
  const createPending = useRef(false);

  const filteredPoints = useMemo(() => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();
    const activeTags = companyListTagsProps.allSelectedTags ?? companyListTagsProps.selectedTags ?? [];
    const selectedTagIds = new Set(activeTags.map((it) => String(it.key)));

    return points.filter((point) => {
      const isSearchMatched =
        normalizedSearchQuery.length === 0 ||
        point.address.toLowerCase().includes(normalizedSearchQuery) ||
        point.comment.toLowerCase().includes(normalizedSearchQuery);
      const isTagsMatched =
        selectedTagIds.size === 0 ||
        Array.from(selectedTagIds).every((tagId) => point.tags?.some((tag) => String(tag.id) === tagId));

      return isSearchMatched && isTagsMatched;
    });
  }, [points, searchQuery, companyListTagsProps.selectedTags, companyListTagsProps.allSelectedTags]);

  const selectedFilteredPointsCount = filteredPoints.filter((point) => selectedPointIds.has(point.id)).length;
  const isSelectAllVisible = filteredPoints.length > 0 && selectedFilteredPointsCount < filteredPoints.length;
  const isResetVisible = selectedFilteredPointsCount > 0;
  const isListLoading = isLoading;
  const isActionsVisible = !isListLoading && (isSelectAllVisible || isResetVisible);
  const isFilteredEmpty = !isListLoading && points.length > 0 && filteredPoints.length === 0;
  const isEmpty = !isListLoading && points.length === 0;

  const handlePointChange = (id: string, isSelected: boolean) => {
    setSelectedPointIds((prevPointIds) => {
      const nextPointIds = new Set(prevPointIds);

      if (isSelected) {
        nextPointIds.add(id);
      } else {
        nextPointIds.delete(id);
      }

      return nextPointIds;
    });
  };

  const handleSelectAllClick = () => {
    setSelectedPointIds((prevPointIds) => {
      const nextPointIds = new Set(prevPointIds);

      filteredPoints.forEach((point) => nextPointIds.add(point.id));

      return nextPointIds;
    });
  };

  const handleResetClick = () => {
    const filteredPointIds = new Set(filteredPoints.map((point) => point.id));

    setSelectedPointIds((prevPointIds) => {
      const nextPointIds = new Set(prevPointIds);

      filteredPointIds.forEach((id) => nextPointIds.delete(id));

      return nextPointIds;
    });
  };

  const handleListScroll = (event: UIEvent<HTMLUListElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;

    if (scrollHeight - scrollTop - clientHeight < 80) {
      loadMore();
    }
  };

  const handleBack = () => {
    dispatch(saveSelectedPointIds([...selectedPointIds]));
    onBack?.();
  };

  const handleCreateClick = async () => {
    if (createPending.current) return;
    const isExactTime = campaignType === AdCampaignTypeEnum.ExactTime;
    const isInterval = campaignType === AdCampaignTypeEnum.Interval;
    const isPeriod = campaignType === AdCampaignTypeEnum.Period;

    const startDate = startDateStr ? new Date(startDateStr) : DateUtils.getStartOfDay(DateUtils.NOW);
    const endDate = endDateStr ? new Date(endDateStr) : DateUtils.getEndOfDay(DateUtils.NOW);

    const validStatuses = promoState.data.rawStatuses.filter((item) => item.promoId);
    const promoIds = validStatuses.map((item) => item.promoId!);
    if (
      mediaType === AdCampaignMediaTypeEnum.Video &&
      !areVideoPromosValid(promoState.data.rawStatuses, isExactTime, timeById, durationById)
    )
      return;
    const audioPromos = validStatuses.map((item) => {
      const entry: { promoId: string; promoAdId: string; time?: string } = {
        promoId: item.promoId!,
        promoAdId: '',
      };
      if (isExactTime && item.id) {
        entry.time = timeById[item.id] || '';
      }
      return entry;
    });
    const promos =
      mediaType === AdCampaignMediaTypeEnum.Video
        ? serializePromoAdInputs(projectVideoPromoDraft(validStatuses, isExactTime, timeById, durationById))
        : audioPromos;

    try {
      createPending.current = true;
      setIsCreating(true);
      const { id } = await createAdCampaign(campaignName, promoIds, companyId, {
        type: campaignType,
        interval: isInterval ? Number(valueByMode) : undefined,
        period: isPeriod ? Number(valueByMode) : undefined,
        startCompany: TimezoneUtils.getDateTimeWithoutTimezone(DateUtils.getStartOfDay(startDate)),
        endCompany: TimezoneUtils.getDateTimeWithoutTimezone(DateUtils.getEndOfDay(endDate)),
        infinitely: isInfinitely,
        startDayTime: dayStartTime,
        endDayTime: dayEndTime,
        days: DateUtils.convertWeekdaysToArray({ ...weekdays }),
        pointIds: Array.from(selectedPointIds),
        mediaType,
        promos,
      });

      if (id) {
        await onSetTagsSubmit(async (tagIds) => {
          await adCampaignSetTags(id, tagIds);
        });

        AnalyticsService.event(EventNameAMP.AD_CAMPAIGN_CREATED, {
          [ParamNameAMP.AD_CAMPAIGN_ID]: id,
          [ParamNameAMP.FLOW]: canBackToPromoRollSynth ? 'promo_roll_synth' : 'default',
        });

        clearAdCampaigns();
        toast.info(NOTIFICATIONS_TEXT.CREATE_SUCCESS);
        navigate(RouterUtils.generatePath(ADS_PAGE_PATH, { id }));
        dispatch(resetCreateAdCampaign());
        promoState.clear();
        close?.();
      } else {
        throw new Error();
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);

      if (errorMessage?.includes(NAME_MAX_LENGTH_ERROR)) {
        toast.error(NOTIFICATIONS_TEXT.MAX_LENGTH_ERROR);
      } else if (error instanceof Error && errorMessage) {
        toast.error(errorMessage);
      } else {
        toast.error(DEFAULT_ERROR_MESSAGE);
      }
    } finally {
      createPending.current = false;
      setIsCreating(false);
    }
  };

  return (
    <ResponsiveDialog
      data-testid="modal_screen"
      open
      width={750}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close?.();
      }}
      contentClassName={styles.container}>
      <div className={styles.headerCard}>
        <ResponsiveDialog.Header className={styles.title}>{TEXTS.TITLE}</ResponsiveDialog.Header>
        <ResponsiveDialog.Close
          id="button_modal_screen_close"
          className={styles.closeButton}
        />
      </div>
      <ResponsiveDialog.Body className={styles.content}>
        <div className={styles.root}>
          <div className={styles.searchRow}>
            <InputSearch
              elementId="input_ad_campaign_stepper_points_search"
              containerClassName={styles.search}
              placeholder={TEXTS.SEARCH_PLACEHOLDER}
              value={searchQuery}
              onChange={setSearchQuery}
            />
            <TagFilters
              pageType="point"
              onlyIcon
              withList={false}
              buttonClassName={styles.filterButton}
              onSetCompanyListTagsProps={setCompanyListTagsProps}
              listRef={listRef}
            />
          </div>

          {(companyListTagsProps.selectedTags?.length ?? 0) > 0 && companyListTagsProps.onDisableTag && (
            <div
              ref={listRef as Ref<HTMLDivElement>}
              className={classNames(styles.tagsRow, companyListTagsProps.showAllTags && styles.tagsRowExpanded)}>
              <CompanyTagsList
                onDisableTag={companyListTagsProps.onDisableTag}
                visibleTitleLength={isDesktop ? VISIBLE_TITLE_LENGTH : VISIBLE_TITLE_LENGTH_MOBILE}
                selectedTags={companyListTagsProps.selectedTags ?? []}
                showAllTags={companyListTagsProps.showAllTags ?? false}
                moreTagsCount={companyListTagsProps.moreTagsCount ?? 0}
                expandCallback={companyListTagsProps.expandCallback!}
                clearAll={companyListTagsProps.clearAll!}
              />
            </div>
          )}

          <div className={styles.divider} />

          {isListLoading && (
            <div className={styles.skeletonActions}>
              {SKELETON_ACTION_KEYS.map((key) => (
                <div
                  key={`skeleton_ad_campaign_stepper_points_action_${key}`}
                  className={styles.skeletonAction}
                />
              ))}
            </div>
          )}

          {isActionsVisible && (
            <div className={styles.actions}>
              {isSelectAllVisible && (
                <button
                  type="button"
                  data-testid="button_ad_campaign_stepper_points_select_all"
                  className={styles.actionButton}
                  onClick={handleSelectAllClick}>
                  {TEXTS.SELECT_ALL}
                </button>
              )}

              {isSelectAllVisible && isResetVisible && <span className={styles.actionSeparator}>•</span>}

              {isResetVisible && (
                <button
                  type="button"
                  data-testid="button_ad_campaign_stepper_points_reset"
                  className={styles.actionButton}
                  onClick={handleResetClick}>
                  {TEXTS.RESET}
                </button>
              )}
            </div>
          )}

          <ul
            className={styles.list}
            onScroll={handleListScroll}>
            {isListLoading &&
              SKELETON_ITEM_KEYS.map((key) => (
                <li
                  key={`skeleton_ad_campaign_stepper_points_${key}`}
                  className={styles.skeletonItem}>
                  <div className={styles.skeletonContent}>
                    <div className={classNames(styles.skeletonLine, styles.skeletonLineLong)} />
                    <div className={classNames(styles.skeletonLine, styles.skeletonLineMedium)} />
                    <div className={classNames(styles.skeletonLine, styles.skeletonLineShort)} />
                  </div>
                  <div className={styles.skeletonSwitcher} />
                </li>
              ))}

            {!isListLoading &&
              filteredPoints.map((point) => (
                <li
                  key={point.id}
                  className={styles.item}>
                  <PointListItemContent
                    currentItem={point}
                    isLoading={isLoading}
                    className={styles.itemContent}
                  />

                  <Switcher
                    elementId={`switch_ad_campaign_stepper_points_${point.id}`}
                    className={styles.switcher}
                    labelClassName={styles.switcherLabel}
                    isChecked={selectedPointIds.has(point.id)}
                    onChange={(isSelected) => handlePointChange(point.id, isSelected)}
                  />
                </li>
              ))}
          </ul>

          {isEmpty && <div className={styles.placeholder}>{TEXTS.EMPTY}</div>}
          {isFilteredEmpty && <div className={styles.placeholder}>{TEXTS.EMPTY_FILTERED}</div>}
        </div>
      </ResponsiveDialog.Body>
      <ResponsiveDialog.Footer className={styles.footerCard}>
        <Button
          elementId="button_ad_campaign_stepper_points_back"
          type={ButtonType.SECONDARY}
          className={styles.footerButton}
          onClick={handleBack}>
          {TEXTS.BACK}
        </Button>
        <Button
          elementId="button_ad_campaign_stepper_points_save"
          type={ButtonType.PRIMARY}
          isDisabled={isListLoading || isCreating}
          isLoading={isCreating || adCampaignCreateLoading}
          className={styles.footerButton}
          onClick={handleCreateClick}>
          {TEXTS.CREATE}
        </Button>
      </ResponsiveDialog.Footer>
    </ResponsiveDialog>
  );
};

export default AdCampaignStepperPoints;
